// Profile.cshtml: the processor-held profile (EditProfile flag) and the QOR sign-in password.
import { PasswordField, TwoFactorSettings, useFeatures } from '@qor/web-shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { cardApi, changePassword, type Profile } from '../api';
import { Alert, NO_OUTCOME, type Outcome } from '../components/Alert';
import { usePageTitle } from '../components/CustomerLayout';
import { useUser } from '../components/RequireAuth';

export const PASSWORD_MESSAGES = {
  tooShort: 'New password must be at least 8 characters.',
  mismatch: "New passwords don't match.",
  changed: 'Your password has been changed.',
} as const;

/** ProfileModel.OnPostChangePasswordAsync's checks before the API is asked. */
export function checkNewPassword(newPassword: string, confirm: string): string | null {
  if (newPassword.length < 8) return PASSWORD_MESSAGES.tooShort;
  if (newPassword !== confirm) return PASSWORD_MESSAGES.mismatch;
  return null;
}

const EMPTY: Profile = { firstName: '', lastName: '', email: '', phoneNumber: '' };

export function ProfilePage() {
  const user = useUser();
  usePageTitle('Profile');
  if (!user.providerUserReference) return <Navigate to="/card" replace />;
  return <ProfileView reference={user.providerUserReference} />;
}

function ProfileView({ reference }: { reference: string }) {
  const user = useUser();
  const features = useFeatures(user.email);
  const profile = useQuery({ queryKey: ['card', reference, 'profile'], queryFn: () => cardApi.profile(reference) });
  if (!features.loaded || profile.isPending) return null;
  // no profile from the processor: empty fields, as the legacy page
  const initial = profile.data ? { ...profile.data, phoneNumber: profile.data.phoneNumber ?? '' } : EMPTY;
  return <ProfileForms reference={reference} initial={initial} editProfile={features.isEnabled('EditProfile')} />;
}

function ProfileForms({
  reference,
  initial,
  editProfile,
}: {
  reference: string;
  initial: Profile;
  editProfile: boolean;
}) {
  const user = useUser();
  const [form, setForm] = useState<Profile>(initial);
  const [profileOutcome, setProfileOutcome] = useState<Outcome>(NO_OUTCOME);
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [passwordOutcome, setPasswordOutcome] = useState<Outcome>(NO_OUTCOME);
  const [busy, setBusy] = useState(false);

  const field = (name: keyof Profile) => ({
    name,
    value: form[name] ?? '',
    onChange: (e: { target: { value: string } }) => setForm({ ...form, [name]: e.target.value }),
  });

  async function saveProfile(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await cardApi.updateProfile(reference, { ...form, phoneNumber: form.phoneNumber || null });
      setProfileOutcome({
        ok: result.success,
        message: result.success ? 'Your profile has been updated.' : result.message,
      });
    } finally {
      setBusy(false);
    }
  }

  async function savePassword(e: FormEvent) {
    e.preventDefault();
    const problem = checkNewPassword(passwords.next, passwords.confirm);
    setPasswords({ current: '', next: '', confirm: '' });
    if (problem) return setPasswordOutcome({ ok: false, message: problem });
    setBusy(true);
    try {
      const result = await changePassword(user.email, passwords.current, passwords.next);
      setPasswordOutcome({ ok: result.success, message: result.success ? PASSWORD_MESSAGES.changed : result.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1 className="qcw-greeting">Profile</h1>
      <p className="qcw-subgreeting">Manage your personal information and sign-in password.</p>

      {editProfile && (
        <section className="qcw-section" aria-label="Profile information">
          <h2 className="qcw-section-title">Personal information</h2>
          <div className="qcw-section-body">
            <Alert {...profileOutcome} />
            <form onSubmit={(e) => void saveProfile(e)} className="qcw-profile-form">
              <div className="qcw-profile-row">
                <label className="qcw-profile-field">
                  <span>First name</span>
                  <input {...field('firstName')} required />
                </label>
                <label className="qcw-profile-field">
                  <span>Last name</span>
                  <input {...field('lastName')} required />
                </label>
              </div>
              <div className="qcw-profile-row">
                <label className="qcw-profile-field">
                  <span>Email</span>
                  <input {...field('email')} type="email" required />
                </label>
                <label className="qcw-profile-field">
                  <span>Phone number</span>
                  <input {...field('phoneNumber')} type="tel" />
                </label>
              </div>
              <button type="submit" className="qcw-btn-primary" disabled={busy}>
                Save changes
              </button>
            </form>
          </div>
        </section>
      )}

      <section className="qcw-section" aria-label="Password">
        <h2 className="qcw-section-title">Password</h2>
        <div className="qcw-section-body">
          <Alert {...passwordOutcome} />
          <form onSubmit={(e) => void savePassword(e)} className="qcw-profile-form">
            <PasswordField
              className="qcw-profile-field"
              label="Current password"
              name="CurrentPassword"
              autoComplete="current-password"
              required
              value={passwords.current}
              onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
            />
            <div className="qcw-profile-row">
              <PasswordField
                className="qcw-profile-field"
                label="New password"
                name="NewPassword"
                autoComplete="new-password"
                minLength={8}
                required
                value={passwords.next}
                onChange={(e) => setPasswords({ ...passwords, next: e.target.value })}
              />
              <PasswordField
                className="qcw-profile-field"
                label="Confirm new password"
                name="ConfirmNewPassword"
                autoComplete="new-password"
                minLength={8}
                required
                value={passwords.confirm}
                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
              />
            </div>
            <button type="submit" className="qcw-btn-primary" disabled={busy}>
              Change password
            </button>
          </form>
        </div>
      </section>

      <section className="qcw-section" aria-label="Two-factor authentication">
        <h2 className="qcw-section-title">Two-factor authentication</h2>
        <div className="qcw-section-body">
          <TwoFactorSettings
            classes={{ field: 'qcw-profile-field', primary: 'qcw-btn-primary', secondary: 'qcw-btn-ghost' }}
          />
        </div>
      </section>
    </>
  );
}
